import {
    usePointDistortion,
    CurveShape,
    DistortionMode,
} from "./PointDistortionContext";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { MapPin, Upload } from "lucide-react";
import { UndoRedoControls } from "../reusable/UndoRedoControls";

export function PointDistortionControls() {
    const {
        svg,
        points,
        error,
        svgInputRef,
        handleFileLoad,
        removePoint,
        updatePoint,
        viewBoxAttr,
        applyPreset,
        getPresetValues,
        resetParameters,
        undo,
        redo,
        canUndo,
        canRedo,
    } = usePointDistortion();

    const handleIntensityChange = (id: string, v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        updatePoint(id, { intensity: Math.min(100, Math.max(0, numValue)) });
    };

    const handleSizeChange = (id: string, v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        updatePoint(id, { size: Math.min(100, Math.max(0, numValue)) });
    };

    const handleCurveShapeChange = (id: string, shape: CurveShape) => {
        updatePoint(id, { curveShape: shape });
    };

    const handleModeChange = (id: string, mode: DistortionMode) => {
        updatePoint(id, { mode });
    };

    return (
        <div className="panel-controls point-distortion-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Point Distortion</h2>
                    <ToolDescriptionPopover dialogLabel="Point Distortion tool description">
                        <p>
                            Import an SVG and click on it to define distortion
                            points. Shape elements are affected by distance from
                            each point. Modes: randomize, repel, or attract.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="point-distortion"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Source" icon={Upload}>
                    <label className="field-label">SVG File</label>
                    <div className="upload-controls">
                        <button
                            type="button"
                            className="generic-button"
                            onClick={() => svgInputRef.current?.click()}
                        >
                            Choose SVG file...
                        </button>
                        <input
                            ref={svgInputRef}
                            type="file"
                            accept=".svg,image/svg+xml"
                            className="upload-input"
                            onChange={handleFileLoad}
                        />
                    </div>
                    {svg && (
                        <p className="upload-info">
                            File: <strong>{svg.name}</strong>
                            {viewBoxAttr && (
                                <>
                                    {" "}
                                    - viewBox: <code>{viewBoxAttr}</code>
                                </>
                            )}
                        </p>
                    )}
                    {!svg && (
                        <p className="upload-hint">
                            Upload an SVG file to begin. Then click on the
                            image to add distortion points.
                        </p>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Distortion points" icon={MapPin}>
                    {points.length === 0 ? (
                        <p className="upload-hint">
                            No points yet. Click the canvas after loading an SVG.
                        </p>
                    ) : (
                        <>
                            <label className="field-label">
                                Points ({points.length})
                            </label>
                            <div className="points-list">
                                {points.map((point, idx) => (
                                    <div
                                        key={point.id}
                                        className="point-control-group"
                                    >
                                        <div className="point-header">
                                            <span className="point-label">
                                                Point {idx + 1}
                                            </span>
                                            <button
                                                type="button"
                                                className="remove-point-button"
                                                onClick={() =>
                                                    removePoint(point.id)
                                                }
                                                title="Remove point"
                                            >
                                                x
                                            </button>
                                        </div>
                                        <div className="point-controls">
                                            <div className="field-row">
                                                <label className="field-label">
                                                    Intensity (0-100)
                                                    <DebouncedInput
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        step={1}
                                                        value={point.intensity}
                                                        onChange={(v) =>
                                                            handleIntensityChange(
                                                                point.id,
                                                                v,
                                                            )
                                                        }
                                                    />
                                                </label>
                                            </div>
                                            <div className="field-row">
                                                <label className="field-label">
                                                    Size (% of SVG diagonal)
                                                    <DebouncedInput
                                                        type="number"
                                                        min={0}
                                                        max={100}
                                                        step={1}
                                                        value={point.size}
                                                        onChange={(v) =>
                                                            handleSizeChange(
                                                                point.id,
                                                                v,
                                                            )
                                                        }
                                                    />
                                                </label>
                                            </div>
                                            <div className="field-row">
                                                <label className="field-label">
                                                    Mode
                                                    <select
                                                        value={point.mode}
                                                        onChange={(e) =>
                                                            handleModeChange(
                                                                point.id,
                                                                e.target
                                                                    .value as DistortionMode,
                                                            )
                                                        }
                                                        className="curve-shape-select"
                                                    >
                                                        <option value="randomize">
                                                            Randomize
                                                        </option>
                                                        <option value="repel">
                                                            Repel
                                                        </option>
                                                        <option value="attract">
                                                            Attract
                                                        </option>
                                                    </select>
                                                </label>
                                            </div>
                                            <div className="field-row">
                                                <label className="field-label">
                                                    Curve Shape
                                                    <select
                                                        value={
                                                            point.curveShape
                                                        }
                                                        onChange={(e) =>
                                                            handleCurveShapeChange(
                                                                point.id,
                                                                e.target
                                                                    .value as CurveShape,
                                                            )
                                                        }
                                                        className="curve-shape-select"
                                                    >
                                                        <option value="linear">
                                                            Linear
                                                        </option>
                                                        <option value="exponential">
                                                            Exponential
                                                        </option>
                                                        <option value="ease-in-out">
                                                            Ease-in-out
                                                        </option>
                                                        <option value="quadratic">
                                                            Quadratic
                                                        </option>
                                                    </select>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </CollapsibleToolGroup>
            </div>

            {error && <p className="upload-error">{error}</p>}
        </div>
    );
}
